import { Checkbox } from "primereact/checkbox";
import { useMapURLParams } from "../../../../../../hooks/map/useMapParams";
import { useMapSlice } from "../../../../../../stateManagement/hooks/useMapSlice";
import type { MapName } from "../../../../../../stateManagement/reducers/maps/actions";
import type { MapListCheckboxFilter } from "../types";
import { useMapDispatch } from "../../../../../../stateManagement/hooks/useMapDispatch";
import { classNames } from "primereact/utils";
import { patchFilterListCheckboxState } from "./filterListCheckboxFunctions";
import SmallLoader from "../../../../../loaders/SmallLoader";
import axios from "axios";
import { store } from "../../../../../../stateManagement/store";
import { getItemWebStorage, setItemWebStorage } from "../../../../../../features/webStorageSaver";
import { useEffect } from "react";

type FilterListCheckboxProps = {
    filterConfig: MapListCheckboxFilter
}

const captionFilter = (caption: string, searchValue: string) => caption?.toLowerCase().includes(searchValue.toLowerCase())

export default function FilterListCheckbox({ filterConfig }: FilterListCheckboxProps) {
    const dispatchMap = useMapDispatch()
    const { canBeIgnored, canIncludeNull, fieldName, hasSearch } = filterConfig ?? {}
    const { mapName } = useMapURLParams() as { mapName: MapName; mapOption?: string };;
    const mapState = useMapSlice(mapName)
    const { mapFiltersState }: any = mapState ?? {};
    const filterState = mapFiltersState?.[fieldName]
    const { isIgnored, includeNull, values, valuesSource, searchValue } = filterState ?? {}
    const ignoreInputId = `ignore-${fieldName}`
    const nullInputId = `null-${fieldName}`

    const listCheckboxContainerClassNames = classNames({
        "list-checkbox": true,
        "with-search-container": hasSearch,
        "filter-ignore": isIgnored
    })

    const showNullInput =
        canIncludeNull &&
        (!hasSearch || captionFilter('Не указан', searchValue));

    const visibleValues = hasSearch
        ? (values ?? []).filter((valueObj: any) => captionFilter(valueObj.caption, searchValue))
        : (values ?? []);

    const IsIgnoredValueCheckbox = () => (
        <div className="map-hint-filters-checkbox-div">
            <Checkbox
                inputId={ignoreInputId}
                name={ignoreInputId}
                onChange={(event: any) => patchFilterListCheckboxState(mapName, fieldName, { isIgnored: event.checked }, dispatchMap)}
                checked={isIgnored}
            />
            <label htmlFor={ignoreInputId} className="ms-2">Не учитывать</label>
        </div>
    )

    const IncludeNullValueCheckbox = () => (
        <div className="map-hint-filters-checkbox-div">
            <Checkbox
                inputId={nullInputId}
                name={nullInputId}
                onChange={(event: any) => patchFilterListCheckboxState(mapName, fieldName, { includeNull: event.checked }, dispatchMap)}
                checked={includeNull}
            />
            <label htmlFor={nullInputId} className="ms-2">Не указан</label>
        </div>
    )

    useEffect(() => {
        const needLoad = !values && valuesSource;
        if (!needLoad) return;

        const load = async () => {
            const { url, propertyForCaption, propertyForValue, searchParam = 'Name', auth = 'app' } = valuesSource;
            const baseUrl = url.startsWith('http') ? url : `${getItemWebStorage('currentConfig')?.irosRestApiUrl}/${url}`;
            const cacheKey = `mapFiltersListValues-${mapName}-${fieldName}-${baseUrl}`;
            const cached = getItemWebStorage(cacheKey);
            if (Array.isArray(cached) && cached.length) {
                patchFilterListCheckboxState(mapName, fieldName, { values: cached }, dispatchMap);
                return;
            }

            const token = auth === 'app' ? store.getState().auth.tokens.app : undefined;
            try {
                const response = await axios.get(baseUrl, {
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                });
                const list = (response.data?.value ?? response.data ?? []).map((item: any) => ({
                    caption: item?.[propertyForCaption],
                    value: item?.[propertyForValue],
                    checked: false,
                }));
                setItemWebStorage(cacheKey, list);
                patchFilterListCheckboxState(mapName, fieldName, { values: list }, dispatchMap);
            } catch (e) {
                // leave values undefined -> spinner shown
            }
        };
        load();
    }, [values, valuesSource, mapName, fieldName, dispatchMap]);

    return (
        <div className="filter-list-checkbox">
            {hasSearch && canBeIgnored && <IsIgnoredValueCheckbox />}
            {hasSearch &&
                <input
                    onChange={(e: any) => patchFilterListCheckboxState(mapName, fieldName, { searchValue: e.target.value }, dispatchMap)}
                    value={searchValue}
                    className="list-checkbox-search"
                />
            }
            <div className={listCheckboxContainerClassNames}>
                {hasSearch && showNullInput && <IncludeNullValueCheckbox />}
                {values
                    ?
                    <>
                        {visibleValues?.map((valueObject: any) => {
                            const { caption, value, checked, color } = valueObject ?? {}
                            const valueInputId = `${value}`
                            return (
                                <div className="map-hint-filters-checkbox-div" style={{ '--checkbox-color': color } as React.CSSProperties}>
                                    <Checkbox
                                        inputId={valueInputId}
                                        name={valueInputId}
                                        onChange={(event: any) => patchFilterListCheckboxState(mapName, fieldName, { toggleValue: { value, checked: event.checked } }, dispatchMap)}
                                        checked={checked}
                                    />
                                    <label htmlFor={valueInputId} className="ms-2">{caption}</label>
                                </div>
                            )
                        })}
                        {!hasSearch && showNullInput && <IncludeNullValueCheckbox />}
                        {!hasSearch && canBeIgnored && <IsIgnoredValueCheckbox />}
                    </>
                    :
                    <div className="map-hint-filters-checkbox-div">
                        <SmallLoader />
                    </div>
                }
                {visibleValues?.length === 0 && !showNullInput &&
                    <div className="map-hint-filters-checkbox-div">
                        <label className="no-results">Нет результатов</label>
                    </div>
                }
            </div>
        </div>
    )
}
